import { FillerPreviewContext, EMPTY_FILLER_PREVIEWS } from '@/components/preview/FillerPreviewContext';
import type { FillerPreviewSources } from '@/lib/preview/fillerPreviewSources';
import { EXPORT_FPS } from '@/lib/timeline/exportFrameTiming';
import { ProjectClipComposition, type LogoConfig } from "@/components/ProjectClipComposition";
import { Player, PlayerRef } from "@remotion/player";
import { useRef, forwardRef, useImperativeHandle, useState, useEffect, useLayoutEffect, useCallback, useMemo, memo, type ComponentType } from "react";
import { renderPreviewStillDataUrl } from '@/lib/render/renderPreviewStillDataUrl';
import { buildPreviewStillInputProps } from '@/lib/cover/buildPreviewStillInputProps';
import { useWatermarkVisibility } from '@/hooks/use-watermark-visibility';
import { useWatermarkPositionContext } from '@/components/remotion/watermark/WatermarkPositionContext';
import type { WatermarkPosition } from '@/lib/watermark/watermarkPosition';
import { PreviewLoadingOverlay } from '@/components/preview/PreviewLoadingOverlay';
import type { PostLayoutConfig } from '@/lib/layouts/types';
import { buildSceneLayout } from '@/lib/transitions/buildSceneLayout';
import { outputFrameToSourceFrame, sourceTimeToOutputTime } from '@/lib/transitions/mapTimes';
import type { SceneTransition, KeyframeTransition } from '@/lib/transitions/types';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, VolumeX } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { SubtitleShadowStyle } from '@/lib/subtitles/subtitleOutline';
import { PREVIEW_SHARED_AUDIO_TAGS } from '@/lib/preview/previewAudioTags';

interface Word {
  word: string;
  start: number;
  end: number;
  start_time?: number;
  end_time?: number;
  startTime?: number;
  endTime?: number;
  isWithinInterval?: boolean;
  gradientOpacity?: number;
}

interface Segment {
  words: Word[];
  startTime: number;
  endTime: number;
  intervalStartTime: number;
}

interface PresetChange {
  type: 'set-value' | 'reorganize-words';
  attribute?: string;
  value?: any;
  settings?: any;
}

interface SubtitlePreset {
  id: string;
  name: string;
  preview: string;
  preset: PresetChange[];
  editable: boolean;
  default: boolean;
}

interface ProjectPreviewProps {
  videoUrl: string;
  startTime?: number;
  endTime?: number;
  intervals?: Array<{ startTime: number; endTime: number }>;
  width?: number;
  height?: number;
  autoPlay?: boolean;
  loop?: boolean;
  volume?: number;
  /**
   * Start the Player muted without locking volume at 0. Unmuted Remotion
   * autoPlay after a setState mount can hang AudioContext.resume() and freeze
   * the clock at 0:00. Clip cards avoid that by mounting and calling play()
   * in the same click (see startClipCardPlayback) so they start with audio.
   */
  startMuted?: boolean;
  onPlayStateChange?: (isPlaying: boolean) => void;
  onMuteStateChange?: (isMuted: boolean) => void;
  onVolumeChange?: (volume: number) => void;
  onSeek?: (time: number) => void;
  style?: React.CSSProperties;
  // Subtitle props
  subtitlesEnabled?: boolean;
  subtitleStyle?: string;
  transcriptionSegments?: Segment[][];
  selectedSubtitlePreset?: SubtitlePreset;
  subtitleConfig?: {
    fontFamily?: string;
    fontUrl?: string | null;
    fontSize?: number;
    textColor?: string;
    highlightColor?: string;
    highlightEnabled?: boolean;
    highlightBoxEnabled?: boolean;
    highlightBoxBackgroundColor?: string;
    highlightBoxBorderRadius?: number;
    highlightBoxPaddingX?: number;
    highlightBoxPaddingY?: number;
    backgroundColor?: string;
    backgroundEnabled?: boolean;
    backgroundOpacity?: number;
    backgroundWidth?: number;
    backgroundHeight?: number;
    position?: string;
    marginVertical?: number;
    marginHorizontal?: number;
    shadowEnabled?: boolean;
    shadowColor?: string;
    shadowSize?: number;
    strokeEnabled?: boolean;
    strokeColor?: string;
    strokeWidth?: number;
    shadowStyle?: SubtitleShadowStyle;
    shadowOffsetX?: number;
    shadowOffsetY?: number;
    shadowSpread?: number;
    animation?: string;
    textAlign?: string;
    textTransform?: string;
    lineSpacing?: number;
    animationSpeed?: number;
    maxWordsPerLine?: number;
    removePunctuation?: boolean;
  };
  // Full video mode props
  isVideoLocked?: boolean;
  fullVideoDuration?: number;
  // Logo props
  logoConfig?: LogoConfig;
  // Safe zone props
  showSafeZone?: boolean;
  showVolumeControls?: boolean;
  /** When false, hide Remotion play/seek/time chrome (template mini cards). Default true. */
  showPlayerControls?: boolean;
  /** When false, skip Remotion's buffering spinner poster. Template mini
   *  cards prefer the first decoded frame over a covering spinner. Default true. */
  showBufferingPoster?: boolean;
  // Crop center intervals for 9:16 preview (matches worker localDetectSpeakerChange output)
  cropCenterKeyframes?: Array<{ time: number; endTime: number; x: number; y: number }>;
  // Face detection overlay for portrait mode (rendered inside composition)
  activeFaces?: Array<{ face_id: string; x: number; y: number; width: number; height: number; is_speaking?: boolean; is_main_speaker?: boolean }>;
  speakerColors?: Record<string, string>;
  // Multi-speaker layout
  speakerLayout?: {
    numSpeakers: number;
    speakers: Array<{
      face_id: string;
      scenes: Array<{ start: number; end: number; cropCenter: { x: number; y: number } }>;
    }>;
    sceneTimeline?: Array<{ start: number; end: number; faceIds: string[] }>;
  };
  // Watermark overlay for free-plan users
  showWatermark?: boolean;
  /** Free-plan watermark position; falls back to WatermarkPositionContext. */
  watermarkPosition?: WatermarkPosition | null;
  /** The project's id. Used to fetch campaign watermark overrides. */
  projectDocumentId?: string;
  /** The library_project's id. Used to fetch the same campaign watermark
   * overrides for library-based preview surfaces. Either projectDocumentId
   * or libraryDocumentId may be provided; if both, projectDocumentId wins. */
  libraryDocumentId?: string;
  // Post-frame layout overlay (registry-driven)
  postLayout?: PostLayoutConfig | null;
  /** Still used for extra layout clip slots in the editor Player so those
   *  cells do not mount another live decoder of the source video. */
  posterUrl?: string | null;
  fillerPreviewSources?: FillerPreviewSources;
  // Media layers (image overlays) authored in the editor
  layers?: import('@/lib/layers/types').LayerOverlayItem[];
  // Per-cut scene transitions
  videoEffects?: unknown;
  transitions?: SceneTransition[];
  // Per-crop-keyframe-boundary transitions (transform family only)
  keyframeTransitions?: KeyframeTransition[];
  /** Browser Player defaults to HTML5. Carousel fallback and BrandKitCard
   *  headless render pass `media` so footage uses @remotion/media / Mediabunny. */
  playback?: 'html5' | 'media';
  /** Fired once the preview element can play, so still extraction can start. */
  onPreviewMediaReady?: () => void;
}

export interface ProjectPreviewRef {
  play: () => void;
  pause: () => void;
  togglePlay: () => void;
  toggleMute: () => void;
  setVolume: (volume: number) => void;
  getVolume: () => number;
  isPlaying: () => boolean;
  isMuted: () => boolean;
  seekTo: (frame: number) => void;
  getCurrentFrame: () => number | null;
  getFps: () => number;
  /** Renders the frame the preview currently shows (layout, subtitles,
   *  layers — exactly as drawn) as a JPEG data URL, frame-accurately via
   *  `@remotion/media`. `videoUrl` overrides the (possibly compressed)
   *  preview source; `frame` renders another frame of the Player's timeline
   *  without moving the playhead. Rejects when the still cannot be rendered. */
  renderCurrentFrameStill: (
    options?: { videoUrl?: string; signal?: AbortSignal; frame?: number },
  ) => Promise<string>;
}

const ProjectPreview = forwardRef<ProjectPreviewRef, ProjectPreviewProps>(({
  videoUrl,
  startTime,
  endTime,
  intervals,
  width = 640,
  height = 360,
  autoPlay = true,
  loop = true,
  volume: externalVolume,
  startMuted = false,
  onPlayStateChange,
  onMuteStateChange,
  onVolumeChange,
  onSeek,
  style,
  subtitlesEnabled,
  subtitleStyle,
  transcriptionSegments,
  selectedSubtitlePreset,
  subtitleConfig,
  isVideoLocked = true,
  fullVideoDuration,
  logoConfig,
  showSafeZone = false,
  showVolumeControls = true,
  showPlayerControls = true,
  showBufferingPoster = true,
  cropCenterKeyframes,
  activeFaces,
  speakerColors,
  speakerLayout,
  showWatermark,
  watermarkPosition,
  projectDocumentId,
  libraryDocumentId,
  postLayout,
  posterUrl,
  fillerPreviewSources = EMPTY_FILLER_PREVIEWS,
  layers,
  transitions,
  videoEffects,
  keyframeTransitions,
  playback,
  onPreviewMediaReady,
}, ref) => {
  const { t } = useTranslation();
  const { campaignWatermark, effectiveShowWatermark } = useWatermarkVisibility({
    showWatermark,
    projectDocumentId,
    libraryDocumentId,
  });
  const contextWatermarkPosition = useWatermarkPositionContext();
  const effectiveWatermarkPosition =
    watermarkPosition !== undefined ? watermarkPosition : contextWatermarkPosition;
  const playerRef = useRef<PlayerRef>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(externalVolume === 0);
  const [internalVolume, setInternalVolume] = useState(1);
  const [playbackError, setPlaybackError] = useState<Error | null>(null);
  const [playerInstanceKey, setPlayerInstanceKey] = useState(0);
  const userUnmutedRef = useRef(false);
  const forcedMuteRef = useRef(false);

  // Use external volume if provided, otherwise use internal volume
  const volume = externalVolume !== undefined ? externalVolume : internalVolume;
  const effectiveMuted = isMuted || volume === 0;
  const lastVolumeRef = useRef<number>(1);
  const lastMutedRef = useRef<boolean>(false);
  const isVideoLockedRef = useRef(isVideoLocked);
  const latestSourceFrameRef = useRef<number | null>(null);
  const onSeekRef = useRef(onSeek);
  const onPlayStateChangeRef = useRef(onPlayStateChange);
  const lastReportedTimeRef = useRef<number>(-1);
  const lastReportedAtRef = useRef<number>(0);

  useEffect(() => {
    const player = playerRef.current;
    if (!player) return;
    const shouldMute = effectiveMuted || (startMuted && !userUnmutedRef.current);
    if (shouldMute) {
      player.mute();
      forcedMuteRef.current = true;
      return;
    }
    // Calling unmute() on an already-audible mount hangs AudioContext.resume()
    // after the click and freezes the clock at 0:00.
    if (forcedMuteRef.current) {
      player.unmute();
      forcedMuteRef.current = false;
    }
  }, [effectiveMuted, playerInstanceKey, startMuted]);

  const handleMediaError = useCallback((error: Error) => {
    holdingPlaybackRef.current = false;
    playerRef.current?.pause();
    setIsPlaying(false);
    setPlaybackError(error);
    onPlayStateChangeRef.current?.(false);
  }, []);

  const isPlayingRef = useRef(false);
  isPlayingRef.current = isPlaying;
  const holdingPlaybackRef = useRef(false);
  const pausingForBufferingRef = useRef(false);
  const seekingFrameRef = useRef(false);
  const seekFrame = useCallback((frame: number) => {
    const player = playerRef.current;
    if (!player) return;
    // Remotion's seekTo pauses synchronously and resumes in its next effect.
    // That internal pause must not switch the editor back to source navigation.
    seekingFrameRef.current = true;
    try {
      player.seekTo(frame);
    } finally {
      seekingFrameRef.current = false;
    }
  }, []);
  const onPreviewBuffering = useCallback((buffering: boolean) => {
    const player = playerRef.current;
    if (!player) return;
    if (buffering) {
      if (!isPlayingRef.current) return;
      holdingPlaybackRef.current = true;
      pausingForBufferingRef.current = true;
      try {
        player.pause();
      } finally {
        pausingForBufferingRef.current = false;
      }
      return;
    }
    if (!holdingPlaybackRef.current) return;
    holdingPlaybackRef.current = false;
    player.play();
  }, []);

  const retryMuted = useCallback(() => {
    setIsMuted(true);
    setPlaybackError(null);
    setPlayerInstanceKey((key) => key + 1);
    onMuteStateChange?.(true);
  }, [onMuteStateChange]);

  useEffect(() => {
    setPlaybackError(null);
    setPlayerInstanceKey(0);
  }, [videoUrl]);

  // Scene layout (with transition overlaps) used to map the Player's output
  // frames back to absolute source time — must match the composition's own
  // layout so playhead/word-highlight/seek round-trips stay accurate once
  // overlap transitions pull scenes forward.
  const reportSceneLayout = useMemo(() => {
    const items = (intervals && intervals.length > 0)
      ? intervals
      : (typeof startTime === 'number' && typeof endTime === 'number'
          ? [{ startTime, endTime }]
          : []);
    return { ...buildSceneLayout({ scenes: items, transitions: transitions ?? [], fps: EXPORT_FPS }), scenes: items };
  }, [intervals, startTime, endTime, transitions]);
  const sceneLayoutRef = useRef(reportSceneLayout);
  // Source timestamps cannot identify which occurrence of copied footage was
  // playing. Retain the exact output frame while paused at that same source.
  const selectedPositionRef = useRef<{
    frame: number;
    sourceFrame: number;
    layout: typeof reportSceneLayout;
  } | null>(null);

  // Child Player effects may emit seeked while clamping a shortened clip.
  // Install the new source axis before those effects report their frame.
  useLayoutEffect(() => {
    onSeekRef.current = onSeek;
    onPlayStateChangeRef.current = onPlayStateChange;
    sceneLayoutRef.current = reportSceneLayout;
  });

  // Initialize state based on props
  useEffect(() => {
    if (autoPlay) {
      setIsPlaying(true);
    }
  }, [autoPlay]);

  // Monitor volume and mute state changes
  useEffect(() => {
    if (lastVolumeRef.current !== volume) {
      lastVolumeRef.current = volume;
      onVolumeChange?.(volume);

      // Auto-update mute state based on volume
      if (volume === 0 && !isMuted) {
        setIsMuted(true);
        onMuteStateChange?.(true);
      } else if (volume > 0 && isMuted) {
        setIsMuted(false);
        onMuteStateChange?.(false);
      }
    }
  }, [volume, isMuted, onVolumeChange, onMuteStateChange]);

  useEffect(() => {
    if (lastMutedRef.current !== isMuted) {
      lastMutedRef.current = isMuted;
      onMuteStateChange?.(isMuted);
    }
  }, [isMuted, onMuteStateChange]);

  useEffect(() => {
    if (onMuteStateChange) {
      onMuteStateChange(isMuted);
    }
  }, [isMuted, onMuteStateChange]);

  useEffect(() => {
    if (onVolumeChange) {
      onVolumeChange(volume);
    }
  }, [volume, onVolumeChange]);

  const reportTime = useCallback((frame: number, force: boolean) => {
    const sourceFrame = isVideoLockedRef.current
      ? outputFrameToSourceFrame(sceneLayoutRef.current.blocks, frame)
      : frame;
    if (isVideoLockedRef.current) {
      selectedPositionRef.current = { frame, sourceFrame, layout: sceneLayoutRef.current };
    } else if (selectedPositionRef.current?.sourceFrame !== sourceFrame) {
      selectedPositionRef.current = null;
    }
    // Keep every frame for axis changes, including frames whose editor update
    // is throttled. A new shorter Player duration can already clamp reads.
    latestSourceFrameRef.current = sourceFrame;
    const absoluteTime = sourceFrame / EXPORT_FPS;
    if (!force) {
      const now = Date.now();
      // 250ms: each report re-renders the whole editor; at 100ms the
      // render cost starved the Player clock and Remotion's drift
      // correction kept seeking the media backwards (visible stutter).
      if (now - lastReportedAtRef.current < 250) return;
      if (Math.abs(absoluteTime - lastReportedTimeRef.current) < 0.05) return;
      lastReportedAtRef.current = now;
    }
    lastReportedTimeRef.current = absoluteTime;
    onSeekRef.current?.(absoluteTime);
  }, []);

  useEffect(() => {
    const onFrameUpdate = (e: { detail: { frame: number } }) => {
      reportTime(e.detail.frame, false);
    };

    const onSeeked = (e: { detail: { frame: number } }) => {
      reportTime(e.detail.frame, true);
    };

    const onPlay = () => {
      isPlayingRef.current = true;
      setIsPlaying(true);
      onPlayStateChangeRef.current?.(true);
    };

    const onPause = () => {
      // Buffering pauses the native clock, not the user's playback intent.
      // Keep the editor on its selected-scene axis until playback resumes.
      if (pausingForBufferingRef.current || seekingFrameRef.current) return;
      holdingPlaybackRef.current = false;
      isPlayingRef.current = false;
      setIsPlaying(false);
      onPlayStateChangeRef.current?.(false);
    };

    const onMuteChange = (e: { detail: { isMuted: boolean } }) => {
      if (!e.detail.isMuted) {
        userUnmutedRef.current = true;
      }
    };

    let attachedPlayer: PlayerRef | null = null;
    let rafId: number | null = null;

    const attach = () => {
      const player = playerRef.current;
      if (!player) {
        rafId = requestAnimationFrame(attach);
        return;
      }
      attachedPlayer = player;
      player.addEventListener('frameupdate', onFrameUpdate);
      player.addEventListener('seeked', onSeeked);
      player.addEventListener('play', onPlay);
      player.addEventListener('pause', onPause);
      player.addEventListener('mutechange', onMuteChange);
    };
    attach();

    return () => {
      if (rafId !== null) cancelAnimationFrame(rafId);
      if (attachedPlayer) {
        attachedPlayer.removeEventListener('frameupdate', onFrameUpdate);
        attachedPlayer.removeEventListener('seeked', onSeeked);
        attachedPlayer.removeEventListener('play', onPlay);
        attachedPlayer.removeEventListener('pause', onPause);
        attachedPlayer.removeEventListener('mutechange', onMuteChange);
      }
    };
  }, [reportTime]);

  useImperativeHandle(ref, () => ({
    play: () => {
      if (playerRef.current) {
        playerRef.current.play();
        setIsPlaying(true);
        onPlayStateChange?.(true);
      }
    },
    pause: () => {
      holdingPlaybackRef.current = false;
      if (playerRef.current) {
        playerRef.current.pause();
        setIsPlaying(false);
        onPlayStateChange?.(false);
      }
    },
    togglePlay: () => {
      if (isPlaying) {
        holdingPlaybackRef.current = false;
        if (playerRef.current) {
          playerRef.current.pause();
          setIsPlaying(false);
          onPlayStateChange?.(false);
        }
      } else {
        if (playerRef.current) {
          playerRef.current.play();
          setIsPlaying(true);
          onPlayStateChange?.(true);
        }
      }
    },
    toggleMute: () => {
      const newMutedState = !isMuted;
      setIsMuted(newMutedState);
      onMuteStateChange?.(newMutedState);

      // Set volume to 0 when muting, restore previous volume when unmuting
      if (newMutedState) {
        if (externalVolume === undefined) {
          setInternalVolume(0);
        }
        onVolumeChange?.(0);
      } else {
        // Restore to a default volume when unmuting (if volume was 0)
        const newVolume = volume === 0 ? 0.5 : volume;
        if (externalVolume === undefined) {
          setInternalVolume(newVolume);
        }
        onVolumeChange?.(newVolume);
      }
    },
    setVolume: (newVolume: number) => {
      const clampedVolume = Math.max(0, Math.min(1, newVolume));
      if (externalVolume === undefined) {
        setInternalVolume(clampedVolume);
      }
      onVolumeChange?.(clampedVolume);

      // Auto-mute when volume is 0, auto-unmute when volume > 0
      if (clampedVolume === 0 && !isMuted) {
        setIsMuted(true);
        onMuteStateChange?.(true);
      } else if (clampedVolume > 0 && isMuted) {
        setIsMuted(false);
        onMuteStateChange?.(false);
      }
    },
    getVolume: () => volume,
    isPlaying: () => isPlaying,
    isMuted: () => isMuted,
    seekTo: seekFrame,
    getCurrentFrame: () => playerRef.current?.getCurrentFrame?.() ?? null,
    getFps: () => 30,
    renderCurrentFrameStill: (options = {}) => {
      const latest = stillSourceRef.current;
      return renderPreviewStillDataUrl({
        component: ProjectClipComposition as ComponentType<Record<string, unknown>>,
        inputProps: buildPreviewStillInputProps(latest.inputProps, { videoUrl: options.videoUrl }),
        width: latest.width,
        height: latest.height,
        fps: EXPORT_FPS,
        durationInFrames: latest.durationInFrames,
        frame: options.frame ?? playerRef.current?.getCurrentFrame?.() ?? 0,
        signal: options.signal,
      });
    },
  }), [isPlaying, isMuted, volume, seekFrame, onPlayStateChange, onMuteStateChange, onVolumeChange]);

  const playerInputProps = useMemo(() => ({
    videoUrl, startTime, endTime, intervals, isMuted, volume, subtitlesEnabled, subtitleStyle, transcriptionSegments, selectedSubtitlePreset, subtitleConfig, isVideoLocked, fullVideoDuration, logoConfig, showSafeZone, cropCenterKeyframes, activeFaces, speakerColors, speakerLayout, showWatermark: effectiveShowWatermark, campaignWatermark, watermarkPosition: effectiveWatermarkPosition, postLayout, posterUrl, layers, videoEffects, transitions, keyframeTransitions, playback, onMediaError: handleMediaError, previewPlaying: isPlaying, onPreviewBuffering, onPreviewMediaReady,
  }), [videoUrl, startTime, endTime, intervals, isMuted, volume, subtitlesEnabled, subtitleStyle, transcriptionSegments, selectedSubtitlePreset, subtitleConfig, isVideoLocked, fullVideoDuration, logoConfig, showSafeZone, cropCenterKeyframes, activeFaces, speakerColors, speakerLayout, effectiveShowWatermark, campaignWatermark, effectiveWatermarkPosition, postLayout, posterUrl, layers, videoEffects, transitions, keyframeTransitions, playback, handleMediaError, isPlaying, onPreviewBuffering, onPreviewMediaReady]);

  const durationInFrames = useMemo(() => {
    const fps = EXPORT_FPS;

    // When video is unlocked, use full video duration
    if (!isVideoLocked && fullVideoDuration) {
      return Math.max(1, Math.ceil(fullVideoDuration * fps));
    }

    // When video is locked, use intervals (original behavior)
    const items = (intervals && intervals.length > 0)
      ? intervals
      : (typeof startTime === 'number' && typeof endTime === 'number'
          ? [{ startTime, endTime }]
          : []);
    const { totalDurationInFrames } = buildSceneLayout({ scenes: items, transitions: transitions ?? [], fps });
    return Math.max(1, totalDurationInFrames);
  }, [isVideoLocked, fullVideoDuration, intervals, startTime, endTime, transitions]);

  // Scene edits re-report the stationary output frame without seeking. A
  // padlock change instead preserves its source position on the new axis.
  useLayoutEffect(() => {
    const axisChanged = isVideoLockedRef.current !== isVideoLocked;
    isVideoLockedRef.current = isVideoLocked;
    const player = playerRef.current;
    if (!player) return;
    let frame = player.getCurrentFrame();
    if (frame == null) return;
    if (axisChanged && latestSourceFrameRef.current != null) {
      const selectedPosition = selectedPositionRef.current;
      frame = isVideoLocked
        ? selectedPosition?.layout === reportSceneLayout && selectedPosition.sourceFrame === latestSourceFrameRef.current
          ? selectedPosition.frame
          : Math.round(sourceTimeToOutputTime(
            reportSceneLayout.scenes, reportSceneLayout.blocks,
            latestSourceFrameRef.current / EXPORT_FPS, EXPORT_FPS,
          ) * EXPORT_FPS)
        : latestSourceFrameRef.current;
      // The padlock changes the Player's axis; move its frame to the same
      // source position before child duration-clamp effects can emit seeked.
      frame = Math.max(0, Math.min(frame, durationInFrames - 1));
      seekFrame(frame);
    }
    reportTime(Math.max(0, Math.min(frame, durationInFrames - 1)), true);
  }, [reportSceneLayout, isVideoLocked, durationInFrames, videoUrl, reportTime, seekFrame]);

  // Read by the imperative `renderCurrentFrameStill`, which is declared
  // before these values exist.
  const stillSourceRef = useRef({ inputProps: playerInputProps as Record<string, unknown>, width, height, durationInFrames });
  stillSourceRef.current = { inputProps: playerInputProps as Record<string, unknown>, width, height, durationInFrames };

  const renderNoMuteButton = useCallback(() => null, []);
  const renderNoVolumeSlider = useCallback(() => null, []);
  const renderPreviewLoading = useCallback(() => <PreviewLoadingOverlay />, []);

  if (!videoUrl) {
    return (
      <div
        className="bg-muted flex items-center justify-center rounded-lg"
        style={{ width, height }}
      >
        <div className="text-center">
          <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1m4 0h1m-6 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <p className="text-sm text-muted-foreground">No video available</p>
        </div>
      </div>
    );
  }

  if (playbackError) {
    return (
      <div
        role="alert"
        className="flex h-full w-full items-center justify-center rounded-lg bg-black p-6 text-white"
        style={style}
      >
        <div className="max-w-sm text-center">
          <AlertTriangle className="mx-auto mb-3 h-8 w-8 text-amber-400" />
          <p className="font-semibold">{t('projectPreview.playbackErrorTitle')}</p>
          <p className="mt-1 text-sm text-white/70">
            {t('projectPreview.playbackErrorDescription')}
          </p>
          <Button type="button" className="mt-4" onClick={retryMuted}>
            <VolumeX className="mr-2 h-4 w-4" />
            {t('projectPreview.retryMuted')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg overflow-hidden w-full h-full">
      <FillerPreviewContext.Provider value={fillerPreviewSources}>
      <Player
        key={playerInstanceKey}
        ref={playerRef}
        component={ProjectClipComposition}
        inputProps={playerInputProps}
        initiallyMuted={startMuted || effectiveMuted}
        durationInFrames={durationInFrames}
        compositionWidth={width}
        compositionHeight={height}
        fps={EXPORT_FPS}
        autoPlay={autoPlay}
        loop={loop}
        controls={showPlayerControls}
        clickToPlay={showPlayerControls}
        renderLoading={renderPreviewLoading}
        renderPoster={showBufferingPoster ? renderPreviewLoading : undefined}
        showPosterWhenBuffering={showBufferingPoster}
        showPosterWhenBufferingAndPaused={showBufferingPoster}
        renderMuteButton={showVolumeControls ? undefined : renderNoMuteButton}
        renderVolumeSlider={showVolumeControls ? undefined : renderNoVolumeSlider}
        numberOfSharedAudioTags={PREVIEW_SHARED_AUDIO_TAGS}
        style={style}
      />
      </FillerPreviewContext.Provider>
    </div>
  );
});

export default memo(ProjectPreview);
